import CallSplitIcon from '@mui/icons-material/CallSplit';
import {Box, CircularProgress, Paper, Stack, Typography} from '@mui/material';
import type {ColumnDef} from '@tanstack/react-table';
import type {ProfileCluster} from 'crqlar-react-common/dist/models/profile/profileCluster.model';
import type {Profile} from 'crqlar-react-common/dist/models/profile/profile.model';
import {DeduplicationTaskService} from 'crqlar-react-common/dist/services/profile/deduplicationTask.service';
import {ProfileClusterService} from 'crqlar-react-common/dist/services/profile/profileCluster.service';
import {ProfileService} from 'crqlar-react-common/dist/services/profile/profile.service';
import {formatFullDateTime} from 'crqlar-react-common/dist/utils/date.util';
import {formatProfileListName} from 'crqlar-react-common/dist/utils/profile.util';
import {useReadOnly} from 'crqlar-react-common/dist/utils/hooks/users/useReadOnly';
import {useMemo} from 'react';
import {useMutation, useQuery} from 'react-query';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {
    ServerTable,
    ServerTableColumns,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSort,
    ServerTableToolbar,
    ServerTableToolbarAction,
    useServerTable
} from '../../common/serverTable';
import {ProfileClusterActionChip, ProfileClusterTypeChip} from '../profileClusters/ProfileClusterStatusChip';

type ProfileClusterTableRow = ProfileCluster & {
    representativeProfileLabel: string
    childProfileLabels: string[]
};

const getProfileLabel = (profile: Profile | undefined, profileId: string) => {
    if (!profile) return null;

    return [formatProfileListName(profile), profileId.substring(20)].filter(Boolean).join(' ') || null;
};

const getProfiles = async (profileIds: string[]) => {
    if (!profileIds.length) return [];

    const profiles: Profile[] = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
        const page = await ProfileService.getAllProfiles({
            id: profileIds,
            followMerge: false,
            limit: {
                offset,
                limit: 1000,
                sort: [{field: 'name', direction: 'ASC'}]
            }
        });

        profiles.push(...(page.content ?? []));
        hasMore = page.last === false;
        offset += 1000;
    }

    return profiles;
};

const ClusterListTab = ({isTaskRunning}: { isTaskRunning: boolean }) => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const readOnlyUser = useReadOnly();
    const {data: unmergeStatus, refetch: refetchUnmergeStatus} = useQuery({
        queryKey: ['unmergeStatus'],
        queryFn: DeduplicationTaskService.getUnmergeStatus,
        refetchInterval: 2000
    });

    const isUnmerging = unmergeStatus?.status === 'IN_PROGRESS'
        || unmergeStatus?.status === 'PENDING';

    const columns = useMemo<ColumnDef<ProfileClusterTableRow>[]>(() => [
        {
            id: 'clusterId',
            header: t('Cluster ID'),
            accessorFn: cluster => cluster.clusterId,
            cell: ({row}) => <Box sx={{whiteSpace: 'nowrap'}}>{row.original.clusterId || ''}</Box>,
            size: 300,
            minSize: 300,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'representativeProfile',
            header: t('Representative Profile'),
            accessorFn: cluster => cluster.representativeProfileLabel,
            cell: ({row}) => <Box>{row.original.representativeProfileLabel}</Box>,
            size: 220,
            meta: {defaultActive: true}
        },
        {
            id: 'childProfiles',
            header: t('Child Profiles'),
            accessorFn: cluster => cluster.childProfileLabels.join('\n'),
            cell: ({row}) => (
                <Typography sx={{whiteSpace: 'pre-wrap'}}>
                    {row.original.childProfileLabels.length
                        ? row.original.childProfileLabels.join('\n')
                        : '-'}
                </Typography>
            ),
            size: 225,
            meta: {defaultActive: true, grow: true}
        },
        {
            id: 'clusterType',
            header: t('Type'),
            accessorFn: cluster => cluster.clusterType,
            cell: ({row}) => <ProfileClusterTypeChip type={row.original.clusterType}/>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'size',
            header: t('Size'),
            accessorFn: cluster => cluster.size,
            cell: ({row}) => <Box>{row.original.size ?? ''}</Box>,
            size: 100,
            meta: {defaultActive: true, isServerSortable: true, align: 'right'}
        },
        {
            id: 'coherenceScore',
            header: t('Coherence Score'),
            accessorFn: cluster => cluster.coherenceScore,
            cell: ({row}) => <Box>{row.original.coherenceScore?.toFixed(2) ?? ''}</Box>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true, align: 'right'}
        },
        {
            id: 'suggestedAction',
            header: t('Suggested Action'),
            accessorFn: cluster => cluster.suggestedAction,
            cell: ({row}) => <ProfileClusterActionChip action={row.original.suggestedAction}/>,
            size: 220,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'formationReason',
            header: t('Formation Reason'),
            accessorFn: cluster => cluster.formationReason,
            cell: ({row}) => <Box>{row.original.formationReason || ''}</Box>,
            size: 320,
            meta: {defaultActive: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: cluster => cluster.id,
            cell: ({row}) => <Box>{row.original.id || ''}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: cluster => cluster.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);


    const controller = useServerTable({
        tableId: 'system-deduplication-clusters',
        columns,
        getRowId: cluster => cluster.clusterId!,
        enableRowSelection: true,
        onRowClick: cluster => navigate(`/system/profileClusters/${cluster.id}`),
        initialSorting: [{id: 'coherenceScore', desc: true}],
        refetchInterval: isUnmerging ? 5000 : false,
        queryFn: async ({pagination, sorting}) => {
            const page = await ProfileClusterService.getAllProfileClusters({
                minSize: 2,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
            const clusters = page.content ?? [];
            const profileIds = Array.from(new Set(clusters.flatMap(cluster => [
                ...(cluster.profileIds ?? []),
                ...(cluster.representativeProfileId ? [cluster.representativeProfileId] : [])
            ])));
            const profiles = await getProfiles(profileIds);
            const profilesById = new Map(profiles.map(profile => [profile.id, profile]));
            const content = clusters.map<ProfileClusterTableRow>(cluster => ({
                ...cluster,
                representativeProfileLabel: cluster.representativeProfileId
                    ? getProfileLabel(
                    profilesById.get(cluster.representativeProfileId),
                    cluster.representativeProfileId
                ) ?? '-'
                    : '-',
                childProfileLabels: (cluster.profileIds ?? [])
                    .map(profileId => getProfileLabel(profilesById.get(profileId), profileId))
                    .filter((label): label is string => !!label)
            }));

            return {
                content,
                totalElements: page.totalElements ?? 0
            };
        }
    });
    const selectedClusterIds = Object.entries(controller.table.getState().rowSelection)
        .filter(([, selected]) => selected)
        .map(([clusterId]) => clusterId);
    const unmergeSelectedMutation = useMutation({
        mutationFn: (clusterIds: string[]) => DeduplicationTaskService.unmergeByClusterIds(clusterIds),
        onSuccess: () => {
            controller.query.refetch();
            refetchUnmergeStatus();
        }
    });
    const unmergeAllMutation = useMutation({
        mutationFn: DeduplicationTaskService.unmergeAll,
        onSuccess: () => {
            controller.query.refetch();
            refetchUnmergeStatus();
        }
    });


    return (
        <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 0}}>
            {isUnmerging && (
                <Paper sx={{p: 2, mb: 3, bgcolor: 'warning.light'}}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <CircularProgress size={20}/>
                        <Typography variant="body2" fontWeight="medium">
                            {t('Unmerge operation in progress...')}
                            {unmergeStatus?.totalClusters && unmergeStatus?.processedClusters !== undefined && (
                                <> ({unmergeStatus.processedClusters} / {unmergeStatus.totalClusters})</>
                            )}
                        </Typography>
                    </Stack>
                </Paper>
            )}

            {unmergeStatus?.status === 'FAILED' && unmergeStatus?.errorMessage && (
                <Paper sx={{p: 2, mb: 3, bgcolor: 'error.light'}}>
                    <Typography variant="body2" color="error.dark">
                        {t('Unmerge failed')}: {unmergeStatus.errorMessage}
                    </Typography>
                </Paper>
            )}

            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>

                        <ServerTableToolbarAction
                            color="warning"
                            startIcon={<CallSplitIcon/>}
                            disabled={!selectedClusterIds.length || isUnmerging || isTaskRunning || readOnlyUser}
                            loading={unmergeSelectedMutation.isLoading}
                            onClick={() => {
                                unmergeSelectedMutation.mutate(selectedClusterIds);
                                controller.table.resetRowSelection();
                            }}
                        >
                            {t('Unmerge Selected')}{selectedClusterIds.length ? ` (${selectedClusterIds.length})` : ''}
                        </ServerTableToolbarAction>

                        <ServerTableToolbarAction
                            variant="contained"
                            color="warning"
                            startIcon={<CallSplitIcon/>}
                            disabled={isUnmerging || isTaskRunning || readOnlyUser}
                            loading={unmergeAllMutation.isLoading}
                            onClick={() => {
                                unmergeAllMutation.mutate();
                                controller.table.resetRowSelection();
                            }}
                        >
                            {t('Unmerge All')}
                        </ServerTableToolbarAction>
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </Box>
    );
};

export default ClusterListTab;
